import type { RouteLocationRaw } from 'vue-router'
import type { TmIconType, TmMenuItemBadge } from '@/components/shared/types'
import type { IconName } from '@/assets/icons/icons'
import type { Callback } from '@/types'
import { isRecordUnknown } from '@/utils/typeGuards'
import type { Component, ComputedRef, Ref } from '@/composition/vue/compositionApi'
import type { TmWrappers } from '@/wrappers'
import type { TmRoutes } from '@/services/route/routerTypes'

export type PageContentSidebarItemAction = {
  icon: IconName
  text: string
  name?: string
  id?: string // name and id need for correct event emit on front
}

export type PageContentSidebarLink<IdType = string, MetaType = Record<string, unknown>> = {
  itemType: 'link'
  title: string
  route: RouteLocationRaw | TmRoutes
  tooltip?: string
  icon?: IconName
  rightIcon?: TmIconType
  number?: number | null
  numberLimit?: number | null
  id?: IdType
  isLoadingNumber?: boolean
  actions?: PageContentSidebarItemAction[]
  badge?: TmMenuItemBadge
  onClick?: Callback<unknown>
  allowed?: boolean
  disabled?: boolean
  overrideActive?: boolean
  meta?: MetaType
  hidden?: boolean
}

export type PageContentSidebarGroupBase<Type extends string> = {
  itemType: Type
  title: string
  links: Array<PageContentSidebarLink | PageContentSidebarSimpleItem>
  icon?: IconName
  hidden?: boolean
}

export type PageContentSidebarGroup = PageContentSidebarGroupBase<'group'>
export type PageContentSidebarGroupCollapsible<T extends string = string> =
  PageContentSidebarGroupBase<'groupCollapsible'> & {
    isCollapsingDisabled?: boolean
    collapsed?: boolean
    itemKey?: T
    hideCollapsedHr?: boolean
  }
export type PageContentSidebarSimpleItem = {
  id: string
  itemType: 'simple'
  title: string
  isActive?: boolean
  tooltip?: string
  number?: number | null
  numberLimit?: number | null
  redCounter?: boolean
  icon?: IconName
  iconType?: 'icon' | 'img'
  rightIcon?: TmIconType
  isLoadingNumber?: boolean
  badge?: TmMenuItemBadge
  allowed?: boolean
  disabled?: boolean
  hidden?: boolean
  comingSoon?: boolean
  actions?: PageContentSidebarItemAction[]
}

export type PageContentSidebarItem =
  | PageContentSidebarLink
  | PageContentSidebarGroup
  | PageContentSidebarGroupCollapsible
  | PageContentSidebarSimpleItem

export type PageContentSidebarItemTypes = PageContentSidebarItem['itemType']

export const isPageContentSidebarItem = (item: unknown): item is PageContentSidebarItem => {
  return (
    isRecordUnknown(item) &&
    typeof item.itemType === 'string' &&
    ['link', 'group', 'groupCollapsible', 'simple'].includes(item.itemType) &&
    typeof item.title === 'string'
  )
}

export const isPageContentSidebarGroupCollapsible = (
  item: PageContentSidebarItem,
): item is PageContentSidebarGroupCollapsible => {
  return item.itemType === 'groupCollapsible'
}

export const isPageContentSidebarLink = (item: PageContentSidebarItem): item is PageContentSidebarLink => {
  return item.itemType === 'link'
}

export const isPageContentSidebarGroup = (item: PageContentSidebarItem): item is PageContentSidebarGroup => {
  return item.itemType === 'group'
}

export type MainSidebarMenuItemType = 'simple' | 'link' | 'group' | 'collapse' | 'lazyCollapse'

export type MainSidebarMenuItem =
  | MainSidebarMenuLinkItem
  | MainSidebarMenuGroupItem
  | MainSidebarMenuCollapseItem
  | MainSidebarMenuSimpleItem
  | MainSidebarMenuDraggableItem
  | MainSidebarMenuLazyCollapseItem

export type MainSidebarMenuCollapseItem = {
  key: string
  itemType: 'collapse'
  sections: MainSidebarMenuSection[]
  dependencies?: RouteLocationRaw[]
} & MainSidebarMenuItemBase

export type MainSidebarMenuGroupItem = {
  itemType: 'group'
  sections: MainSidebarMenuSection[]
} & MainSidebarMenuItemBase

export type MainSidebarMenuDraggableItem = {
  itemType: 'draggable'
  items: (MainSidebarMenuLinkItem | MainSidebarMenuSimpleItem)[]
  onSort?: (sortedItems: (MainSidebarMenuLinkItem | MainSidebarMenuSimpleItem)[]) => Promise<void>
}

export type MainSidebarMenuLinkItem = {
  itemType: 'link'
  route: RouteLocationRaw
  active?: Ref<boolean>
  isCompareRouteParams?: boolean
} & MainSidebarMenuItemBase

export type MainSidebarMenuSimpleItem = {
  itemType: 'simple'
  onClick: () => void
} & MainSidebarMenuItemBase

export type MainSidebarMenuLazyCollapseItem = {
  itemType: 'lazyCollapse'
  sectionWrapperId: TmWrappers
  useMenu: () => { menu: ComputedRef<MainSidebarMenuItem> }
  activeRoutes?: RouteLocationRaw[]
} & MainSidebarMenuItemBase

type MainSidebarMenuItemBase = {
  id?: string
  title: string
  icon?: IconName
  badge?: Component
  action?: Component
  onClickByAction?: (event: MouseEvent) => Promise<void>
}

export type MainSidebarMenuSection = {
  items: MainSidebarMenuItem[]
  sectionWrapperId?: TmWrappers
  contentComponent?: Component
  header?: Component
  footer?: Component
  emptyState?: Component
  errorComponent?: Component
}
